import type { EquipmentKind } from './types.ts'

export const EQUIPMENT_KINDS: EquipmentKind[] = ['appliance', 'cookware', 'bakeware', 'tool']

/** Tap-to-add catalogue, and the lookup that classifies a bare name. */
export const EQUIPMENT_CATALOGUE: Record<EquipmentKind, string[]> = {
  appliance: [
    'oven', 'stovetop', 'induction hob', 'microwave', 'air fryer', 'pressure cooker',
    'slow cooker', 'rice cooker', 'toaster oven', 'grill', 'broiler', 'sous vide',
    'deep fryer', 'bread machine', 'waffle iron', 'electric kettle',
  ],
  cookware: [
    'nonstick skillet', 'cast iron skillet', 'stainless skillet', 'saucepan', 'stockpot',
    'dutch oven', 'wok', 'griddle', 'grill pan', 'steamer basket', 'roasting pan', 'colander',
  ],
  bakeware: [
    'sheet pan', 'baking dish', 'cake tin', 'loaf tin', 'muffin tin',
    'pie dish', 'springform tin', 'cooling rack', 'parchment paper',
  ],
  tool: [
    'chef knife', 'blender', 'immersion blender', 'food processor', 'stand mixer', 'hand mixer',
    'kitchen scale', 'thermometer', 'mandoline', 'mortar and pestle', 'spice grinder',
    'microplane', 'rolling pin', 'sieve', 'salad spinner', 'pasta machine', 'tongs', 'mixing bowls',
  ],
}

/** Common names people use that are not the catalogue's own wording. */
const ALIASES: Record<string, EquipmentKind> = {
  'instant pot': 'appliance', 'crock pot': 'appliance', 'crockpot': 'appliance',
  'airfryer': 'appliance', 'air-fryer': 'appliance', 'bbq': 'appliance', 'barbecue': 'appliance',
  'hob': 'appliance', 'stove': 'appliance', 'range': 'appliance', 'toaster': 'appliance',
  'skillet': 'cookware', 'frying pan': 'cookware', 'pan': 'cookware', 'pot': 'cookware',
  'casserole dish': 'cookware', 'saucier': 'cookware', 'kadai': 'cookware', 'tagine': 'cookware',
  'baking sheet': 'bakeware', 'baking tray': 'bakeware', 'cake pan': 'bakeware',
  'loaf pan': 'bakeware', 'pizza steel': 'bakeware', 'pizza stone': 'bakeware',
  'knife': 'tool', 'scale': 'tool', 'peeler': 'tool', 'whisk': 'tool', 'grater': 'tool',
}

const LOOKUP: Map<string, EquipmentKind> = new Map(Object.entries(ALIASES))
for (const kind of EQUIPMENT_KINDS) {
  for (const name of EQUIPMENT_CATALOGUE[kind]) LOOKUP.set(name, kind)
}

/**
 * Best guess at what kind of thing a name is. Exact match first, then a word-boundary
 * suffix match so "12-inch cast iron skillet" still lands in cookware.
 */
export function guessKind(name: string, fallback: EquipmentKind = 'tool'): EquipmentKind {
  const n = name.trim().toLowerCase()
  if (!n) return fallback
  const exact = LOOKUP.get(n)
  if (exact) return exact
  let best: { kind: EquipmentKind; len: number } | null = null
  for (const [key, kind] of LOOKUP) {
    if (n === key || n.endsWith(` ${key}`) || n.startsWith(`${key} `) || n.includes(` ${key} `)) {
      if (!best || key.length > best.len) best = { kind, len: key.length }
    }
  }
  return best?.kind ?? fallback
}
